import React from 'react';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import './LegalPage.css';

const NUM_COLOR = '#d97706';

function CancellationRefund() {
  return (
    <div>
      <header className="legal-hero legal-hero--refund">
        <Reveal><span className="legal-hero-badge">💳 Refund Policy</span></Reveal>
        <Reveal delay={80}><h1>Cancellation &amp; Refund Policy</h1></Reveal>
        <Reveal delay={160}><p>Transparent, fair, and customer-friendly refund terms designed with your trust in mind.</p></Reveal>
      </header>

      <div className="legal-body">
        <div className="legal-body-inner">
          <aside className="legal-toc">
            <h4>Contents</h4>
            <ul className="legal-toc-list">
              {['Refund Eligibility','Non-Refundable','Refund Process','Timeline',
                'Course Access','Subscriptions','Special Cases','Contact Us','Amendments','Grievance'].map((t, i) => (
                <li key={t}><a href={`#r${i+1}`}>{i+1}. {t}</a></li>
              ))}
            </ul>
          </aside>

          <div className="legal-content">
            <div className="legal-updated">
              <strong>Last Updated:</strong> May 16, 2026
            </div>
            <p className="legal-intro">
              At Ab Meri Baari, we are committed to ensuring customer satisfaction. This Cancellation and Refund Policy outlines the
              terms and conditions under which course purchases can be cancelled and refunds can be requested.
            </p>

            <Reveal>
              <div className="legal-section" id="r1">
                <div className="legal-section-head">
                  <div className="legal-section-num" style={{ background: NUM_COLOR }}>1</div>
                  <h2>Refund Eligibility</h2>
                </div>
                <ul className="styled-list">
                  <li>The refund request is made within <strong>7 days</strong> of course purchase</li>
                  <li>You have accessed <strong>less than 20%</strong> of the total course content</li>
                  <li>The course has not been completed</li>
                  <li>You have not downloaded or used more than 2 lessons from the course</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={50}>
              <div className="legal-section" id="r2">
                <div className="legal-section-head">
                  <div className="legal-section-num" style={{ background: NUM_COLOR }}>2</div>
                  <h2>Non-Refundable Purchases</h2>
                </div>
                <ul className="styled-list">
                  <li>Courses where more than 20% of content has been accessed</li>
                  <li>Completed courses or courses with quiz/test submissions</li>
                  <li>Purchases made using promotional codes or discounts (unless otherwise specified)</li>
                  <li>Subscription plans after the free trial period (where applicable)</li>
                  <li>Courses purchased more than 7 days ago</li>
                  <li>Bundle purchases where partial content has been accessed</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={100}>
              <div className="legal-section" id="r3">
                <div className="legal-section-head">
                  <div className="legal-section-num" style={{ background: NUM_COLOR }}>3</div>
                  <h2>Refund Process</h2>
                </div>
                <ol className="styled-list">
                  <li>Log in to your Ab Meri Baari account</li>
                  <li>Navigate to "My Purchases" or "My Courses"</li>
                  <li>Select the course you wish to refund</li>
                  <li>Click "Request Refund" and provide the reason</li>
                  <li>Submit the refund request</li>
                  <li>Our support team will review your request within <strong>24-48 hours</strong></li>
                  <li>If approved, the refund will be processed within <strong>5-7 business days</strong></li>
                </ol>
              </div>
            </Reveal>

            <Reveal delay={150}>
              <div className="legal-section" id="r4">
                <div className="legal-section-head">
                  <div className="legal-section-num" style={{ background: NUM_COLOR }}>4</div>
                  <h2>Refund Timeline</h2>
                </div>
                <div className="info-grid">
                  <div className="info-card">
                    <h4>⏱️ Processing Time</h4>
                    <p>Once approved, refunds are processed within 5-7 business days depending on your bank or payment provider.</p>
                  </div>
                  <div className="info-card">
                    <h4>💳 Refund Method</h4>
                    <p>Credited back to the original payment method used during purchase.</p>
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={200}>
              <div className="legal-section" id="r5">
                <div className="legal-section-head">
                  <div className="legal-section-num" style={{ background: NUM_COLOR }}>5</div>
                  <h2>Course Access After Refund Request</h2>
                </div>
                <ul className="styled-list">
                  <li>Your course access will be <strong>suspended immediately</strong></li>
                  <li>You will not be able to access course materials, lectures, or quizzes</li>
                  <li>Downloaded materials may still be accessible on your device, but you forfeit future updates</li>
                  <li>If the refund is rejected, access will be restored within 24 hours</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={250}>
              <div className="legal-section" id="r6">
                <div className="legal-section-head">
                  <div className="legal-section-num" style={{ background: NUM_COLOR }}>6</div>
                  <h2>Cancellation of Subscriptions</h2>
                </div>
                <ul className="styled-list">
                  <li>You can cancel your subscription anytime from your account settings</li>
                  <li>Cancellation takes effect at the end of your billing cycle</li>
                  <li>No refund will be issued for the current billing period unless within the trial period</li>
                  <li>You will retain access to purchased courses until the end of your subscription period</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={300}>
              <div className="legal-section" id="r7">
                <div className="legal-section-head">
                  <div className="legal-section-num" style={{ background: NUM_COLOR }}>7</div>
                  <h2>Special Cases</h2>
                </div>
                <h3>Course Quality Issues</h3>
                <p>If you believe a course has significant technical issues, poor content quality, or does not match the course description, please contact our support team at <a href="mailto:support@abmeribaari.com">support@abmeribaari.com</a> with details. We may offer a full refund or alternative solutions.</p>
                <h3>Duplicate Purchases</h3>
                <p>If you accidentally purchase the same course twice, we will refund the duplicate purchase immediately upon request, regardless of the 7-day window or content access percentage.</p>
                <h3>Technical Issues</h3>
                <p>If you experience technical issues preventing access to a course, we will work with you to resolve the problem. If unresolvable, you may be eligible for a full refund even beyond the 7-day window.</p>
              </div>
            </Reveal>

            <Reveal delay={350}>
              <div className="legal-section" id="r8">
                <div className="legal-section-head">
                  <div className="legal-section-num" style={{ background: NUM_COLOR }}>8</div>
                  <h2>How to Contact Us</h2>
                </div>
                <div className="legal-callout legal-callout--success">
                  <p><strong>Email:</strong> <a href="mailto:support@abmeribaari.com">support@abmeribaari.com</a></p>
                  <p><strong>Phone:</strong> <a href="tel:+919098099553">+91-9098099553</a></p>
                  <p><strong>Response Time:</strong> Within 24 hours on business days</p>
                </div>
              </div>
            </Reveal>

            <Reveal delay={400}>
              <div className="legal-section" id="r9">
                <div className="legal-section-head">
                  <div className="legal-section-num" style={{ background: NUM_COLOR }}>9</div>
                  <h2>Amendments to Policy</h2>
                </div>
                <p>
                  Ab Meri Baari reserves the right to modify this Cancellation and Refund Policy at any time. Any changes will be effective
                  immediately upon posting to this page. We will notify users of significant changes via email. Your continued use of our
                  services implies acceptance of the updated policy.
                </p>
              </div>
            </Reveal>

            <Reveal delay={450}>
              <div className="legal-section" id="r10">
                <div className="legal-section-head">
                  <div className="legal-section-num" style={{ background: NUM_COLOR }}>10</div>
                  <h2>Grievance Resolution</h2>
                </div>
                <p>In case of any dispute regarding a refund decision, you can escalate your complaint to our grievance redressal team. Please send a detailed email to <a href="mailto:grievance@abmeribaari.com">grievance@abmeribaari.com</a> with supporting documents. We will investigate and respond within 7 business days.</p>
                <div className="legal-callout legal-callout--warning" style={{ marginTop: '16px' }}>
                  <strong>Note:</strong> This policy is designed to be fair to both students and the platform. We believe in
                  transparency and customer satisfaction. If you have any questions or concerns, please don't hesitate to contact us.
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default CancellationRefund;
